import { useEffect, useState, type RefObject } from 'react';

const GAP_PX = 8;

/**
 * The height that runs an element from where it starts on the page to the
 * bottom of the screen, less what `reserve` covers there (a fixed bar).
 */
export function useFitToScreen(
  ref: RefObject<HTMLElement | null>,
  reserve: RefObject<HTMLElement | null>,
  enabled: boolean,
  minPx = 256,
): number | undefined {
  const [height, setHeight] = useState<number>();

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const measure = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      const top = el.getBoundingClientRect().top + window.scrollY;
      const covered = (reserve.current?.offsetHeight ?? 0) + GAP_PX;
      const screen = document.documentElement.clientHeight;
      setHeight(Math.max(minPx, Math.floor(screen - top - covered)));
    };
    measure();
    window.addEventListener('resize', measure);
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    observer?.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [ref, reserve, enabled, minPx]);

  return enabled ? height : undefined;
}
